<template>
  <div class="boost-page">
    <div class="boost-shell">
      <header class="topbar">
        <button class="back-btn" type="button" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>
        <h1>{{ pageTitle }}</h1>
        <span class="head-spacer"></span>
      </header>

      <section class="boost-panel">
        <button class="round-toggle" type="button" @click="historyOpen = !historyOpen">
          <span class="round-copy">
            <span>{{ roundLabel }}</span>
            <b class="countdown">{{ countdownText }}</b>
          </span>
          <i class="round-arrow" :class="{ open: historyOpen }"></i>
        </button>

        <div class="selected-box">
          <span>{{ copy.selectedGiftLabel }}</span>
          <b>{{ selectedGiftText }}</b>
        </div>
      </section>

      <section v-if="historyOpen" class="history-panel">
        <div class="history-head">
          <span>{{ copy.historyExpect }}</span>
          <span>{{ copy.historyOpen }}</span>
          <span>{{ copy.historyTime }}</span>
        </div>
        <div class="history-list">
          <div v-for="item in historyRows" :key="`${item.round}-${item.time}`" class="history-row">
            <b>{{ item.round }}</b>
            <span>{{ item.result }}</span>
            <time>{{ item.time }}</time>
          </div>
        </div>
      </section>

      <section class="gift-grid">
        <button
          v-for="gift in displayGifts"
          :key="gift.key"
          class="gift-btn"
          :class="{ on: selectedGiftKeys.includes(gift.key) }"
          type="button"
          @click="toggleGift(gift)"
        >
          <span class="gift-icon">{{ gift.icon }}</span>
          <span class="gift-name">{{ gift.name }}</span>
        </button>
      </section>

      <section class="boost-banner">
        <img src="/lottery-banner.jpg" :alt="pageTitle" />
      </section>

      <section class="amount-wrap">
        <div class="balance-head">
          <div class="balance-pill">
            <span>{{ copy.balanceLabel }}</span>
            <b>{{ balanceText }} {{ copy.unit }}</b>
          </div>
        </div>

        <div class="amount-row">
          <button id="amountInput" type="button" class="amount-input" @click="numPadVisible = true">
            {{ amountDisplay }}
          </button>
          <div class="currency">{{ copy.unit }}</div>
        </div>

        <div class="amount-summary">
          <button id="clearBtn" type="button" class="clear-btn" @click="clearAll">{{ copy.clearText }}</button>
          <div class="total-desc">
            <em>{{ qtyText }}</em>
            <b>{{ totalText }}</b>
          </div>
          <button
            id="submitBtn"
            class="submit-btn"
            :class="{ disabled: submitDisabled }"
            type="button"
            :disabled="submitDisabled || submitting"
            @click="submitOrder"
          >
            {{ submitting ? copy.submittingText : copy.submitText }}
          </button>
        </div>
      </section>
    </div>

    <Footer :is_show="true" />

    <van-popup v-model="numPadVisible" position="bottom" class="num-pad-mask">
      <div class="num-pad">
        <div class="num-pad-head">
          <span>{{ copy.numPadTitle }}</span>
          <button class="num-pad-close" type="button" @click="numPadVisible = false">{{ copy.doneText }}</button>
        </div>
        <div class="num-pad-display">{{ amountDisplay }}</div>
        <div class="num-pad-grid">
          <button v-for="key in keypadKeys" :key="key.value" class="num-key" :class="key.className" type="button" @click="handleKey(key.value)">
            <template v-if="key.value !== 'del'">{{ key.label }}</template>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.85"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M10.1 5.5H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H10.1a2 2 0 0 1-1.52-.7L3.1 12l5.48-5.8a2 2 0 0 1 1.52-.7z"></path>
              <path d="m16.2 9.2-4.6 4.6M11.6 9.2l4.6 4.6"></path>
            </svg>
          </button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import Footer from "@/common/Footer.vue";
import { getPageCopy } from "@/common/pageLocale";

const GIFT_CATALOG = [
  { key: "pretty", icon: "🌹" },
  { key: "sexy", icon: "💗" },
  { key: "charming", icon: "💍" },
  { key: "cute", icon: "🚗" },
];

const FALLBACK_HISTORY = [
  { round: 449, resultKeys: ["pretty", "charming"], time: "22:12:02" },
  { round: 448, resultKeys: ["sexy", "cute"], time: "22:09:04" },
  { round: 447, resultKeys: ["pretty", "cute"], time: "22:06:06" },
  { round: 446, resultKeys: ["sexy", "charming"], time: "22:03:08" },
  { round: 445, resultKeys: ["pretty", "charming"], time: "22:00:10" },
  { round: 444, resultKeys: ["sexy", "cute"], time: "21:57:12" },
  { round: 443, resultKeys: ["pretty", "cute"], time: "21:54:14" },
  { round: 442, resultKeys: ["sexy", "charming"], time: "21:51:16" },
  { round: 441, resultKeys: ["pretty", "charming"], time: "21:48:18" },
  { round: 440, resultKeys: ["sexy", "cute"], time: "21:45:20" },
];

export default {
  components: {
    Footer,
  },
  data() {
    return {
      userInfo: {},
      lottery: {},
      rawHistory: [],
      oddsList: [],
      selectedGiftKeys: [],
      amountInput: "0.00",
      historyOpen: false,
      numPadVisible: false,
      submitting: false,
      countdownSeconds: 0,
      countdownTimer: null,
      refreshTimer: null,
      keypadKeys: [],
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "lottery");
    },
    pageTitle() {
      const lotName = this.lottery.name || this.copy.titleFallback;
      return `${this.copy.titlePrefix} · ${lotName}`;
    },
    roundLabel() {
      const nextExpect = this.lottery.next_expect || this.lottery.now_expect || 450;
      return `${this.copy.roundPrefix}${nextExpect}${this.copy.roundSuffix}`;
    },
    countdownText() {
      const total = Math.max(0, Number(this.countdownSeconds) || 0);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      return [hours, minutes, seconds].map((item) => String(item).padStart(2, "0")).join(":");
    },
    displayGifts() {
      const allowedBackendTypes = Object.values((this.copy && this.copy.backendGiftTypeMap) || {});
      const allowedKeys = GIFT_CATALOG.map((item) => item.key);
      if (Array.isArray(this.oddsList) && this.oddsList.length) {
        return this.oddsList
          .filter((item) => Number(item.status) === 1 || item.status === true)
          .filter((item, index) => {
            const type = String((item && item.type) || "").trim();
            const fallbackKey = GIFT_CATALOG[index] && GIFT_CATALOG[index].key;
            return allowedBackendTypes.includes(type) || allowedKeys.includes(type) || allowedKeys.includes(fallbackKey);
          })
          .map((item, index) => this.normalizeGift(item, index));
      }
      return GIFT_CATALOG.map((item) => ({
        ...item,
        name: this.translateGiftName(item.key),
        type: this.resolveGiftBackendType(item.key),
      }));
    },
    selectedGifts() {
      return this.displayGifts.filter((item) => this.selectedGiftKeys.includes(item.key));
    },
    selectedGiftText() {
      return this.selectedGifts.length ? this.selectedGifts.map((item) => item.name).join(" ") : this.copy.selectedGiftEmpty;
    },
    historyRows() {
      if (Array.isArray(this.rawHistory) && this.rawHistory.length) {
        return this.rawHistory.map((item, index) => this.normalizeHistory(item, index));
      }
      return FALLBACK_HISTORY.map((item) => ({
        ...item,
        result: (item.resultKeys || []).map((name) => this.translateGiftName(name)).join("，"),
      }));
    },
    balanceText() {
      const money = Number((this.userInfo && this.userInfo.money) || 0);
      return money.toFixed(2);
    },
    amountValue() {
      const value = Number(this.amountInput);
      return Number.isFinite(value) ? value : 0;
    },
    amountDisplay() {
      return this.formatMoney(this.amountValue);
    },
    qtyText() {
      return this.copy.qtyText.replace("{count}", this.selectedGifts.length);
    },
    totalAmount() {
      return this.selectedGifts.length * this.amountValue;
    },
    totalText() {
      return this.copy.totalText.replace("{amount}", this.formatMoney(this.totalAmount));
    },
    submitDisabled() {
      return !this.selectedGifts.length || this.amountValue <= 0;
    },
  },
  created() {
    this.keypadKeys = this.getKeypadKeys();
    if (!localStorage.getItem("token")) {
      this.$router.push("/Login");
      return;
    }
    this.fetchPageData();
  },
  beforeDestroy() {
    this.stopCountdown();
    this.stopRefreshTimer();
  },
  methods: {
    back() {
      this.$router.back();
    },
    async fetchPageData() {
      await Promise.all([this.getUserInfo(), this.getLotteryInfo(), this.getLotteryList()]);
      await this.getLotteryPeilv();
    },
    async getUserInfo() {
      const res = await this.$http({
        method: "get",
        url: "user_info",
      });
      if (res && res.code === 200) {
        this.userInfo = res.data || {};
      }
    },
    async getLotteryInfo() {
      const res = await this.$http({
        method: "get",
        data: { key: this.$route.query.key },
        url: "lottery_get_info",
      });
      if (res && res.code === 200 && res.data) {
        this.lottery = Array.isArray(res.data) ? {} : res.data;
        this.startCountdown(Number(this.lottery.second || 0));
      }
    },
    async getLotteryList() {
      const res = await this.$http({
        method: "get",
        data: { key: this.$route.query.key },
        url: "lottery_get_one_list",
      });
      if (res && res.code === 200 && Array.isArray(res.data)) {
        this.rawHistory = res.data;
      } else {
        this.rawHistory = [];
      }
    },
    async getLotteryPeilv() {
      const lotteryId = this.$route.query.id || this.lottery.id;
      if (!lotteryId) {
        this.oddsList = [];
        return;
      }
      const res = await this.$http({
        method: "post",
        data: { id: lotteryId },
        url: "lottery_get_peilv",
      });
      if (res && res.code === 200 && Array.isArray(res.data)) {
        this.oddsList = res.data;
        return;
      }
      this.oddsList = [];
    },
    normalizeGift(item, index) {
      const type = String((item && item.type) || "").trim();
      const fallbackKey = GIFT_CATALOG[index] && GIFT_CATALOG[index].key;
      const rawName = type || String((item && item.name) || "").trim();
      const name = rawName || (fallbackKey ? this.translateGiftName(fallbackKey) : `${this.copy.itemPrefix} ${index + 1}`);
      const icon = this.getGiftIcon(type, index);
      return {
        key: type || fallbackKey || `gift-${index}`,
        name: this.translateGiftName(name),
        type: type || name,
        icon,
      };
    },
    getKeypadKeys() {
      return [
        { value: "1", label: "1" },
        { value: "2", label: "2" },
        { value: "3", label: "3" },
        { value: "4", label: "4" },
        { value: "5", label: "5" },
        { value: "6", label: "6" },
        { value: "7", label: "7" },
        { value: "8", label: "8" },
        { value: "9", label: "9" },
        { value: ".", label: "." },
        { value: "0", label: "0" },
        { value: "del", label: "del" },
        { value: "clear", label: this.copy.keyClear, className: "clear" },
        { value: "ok", label: this.copy.keyOk, className: "ok" },
      ];
    },
    translateGiftName(name) {
      const map = this.copy.giftNameMap || {};
      return map[name] || name;
    },
    resolveGiftBackendType(key) {
      const map = (this.copy && this.copy.backendGiftTypeMap) || {};
      return map[key] || key;
    },
    getGiftIcon(type, index) {
      const backendTypeMap = (this.copy && this.copy.backendGiftTypeMap) || {};
      const resolvedType =
        Object.keys(backendTypeMap).find((key) => backendTypeMap[key] === type) || type;
      const iconMap = {
        pretty: "\u{1F339}",
        sexy: "\u{1F497}",
        charming: "\u{1F48D}",
        cute: "\u{1F697}",
      };
      if (iconMap[resolvedType]) {
        return iconMap[resolvedType];
      }
      const fallbackIcons = ["🌹", "💗", "💍", "🚗", "👑", "🎡", "✈️", "🌌"];
      return fallbackIcons[index % fallbackIcons.length];
    },
    startCountdown(seconds) {
      this.stopCountdown();
      this.stopRefreshTimer();
      this.countdownSeconds = Math.max(0, Number(seconds) || 0);
      this.countdownTimer = setInterval(() => {
        if (this.countdownSeconds > 0) {
          this.countdownSeconds -= 1;
          return;
        }
        this.stopCountdown();
        this.scheduleRefresh();
      }, 1000);
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    scheduleRefresh() {
      this.stopRefreshTimer();
      this.refreshTimer = setTimeout(() => {
        this.fetchPageData();
      }, 1200);
    },
    stopRefreshTimer() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    normalizeHistory(item, index) {
      const round = item.expect || item.round || (450 - index - 1);
      if (item.result && item.time) {
        return {
          round,
          result: item.result,
          time: item.time,
        };
      }
      const codeList = Array.isArray(item.opencode) ? item.opencode : [];
      const gifts = this.mapCodeToGift(codeList);
      const rawTime = Number(item.open_time || item.create_time || 0);
      return {
        round,
        result: gifts.join("，") || this.copy.pendingText,
        time: rawTime ? this.formatClock(rawTime) : "--:--:--",
      };
    },
    mapCodeToGift(codeList) {
      if (!Array.isArray(codeList) || !codeList.length) {
        return [];
      }
      const sum = codeList.reduce((total, code) => total + (Number(code) || 0), 0);
      if (!sum) {
        return [];
      }
      const sizeName = this.translateGiftName(sum >= 11 ? "pretty" : "sexy");
      const parityName = this.translateGiftName(sum % 2 === 0 ? "cute" : "charming");
      return [sizeName, parityName];
    },
    formatClock(timestamp) {
      const time = new Date(timestamp * 1000);
      const hh = String(time.getHours()).padStart(2, "0");
      const mm = String(time.getMinutes()).padStart(2, "0");
      const ss = String(time.getSeconds()).padStart(2, "0");
      return `${hh}:${mm}:${ss}`;
    },
    toggleGift(gift) {
      if (this.selectedGiftKeys.includes(gift.key)) {
        this.selectedGiftKeys = this.selectedGiftKeys.filter((item) => item !== gift.key);
        return;
      }
      this.selectedGiftKeys = this.selectedGiftKeys.concat(gift.key);
    },
    formatMoney(value) {
      const money = Number(value || 0);
      return money.toFixed(2);
    },
    handleKey(key) {
      if (key === "ok") {
        this.numPadVisible = false;
        return;
      }
      if (key === "clear") {
        this.amountInput = "0.00";
        return;
      }
      if (key === "del") {
        this.deleteAmount();
        return;
      }
      this.appendAmount(key);
    },
    appendAmount(key) {
      let raw = this.amountInput;
      if (raw === "0.00") {
        raw = "";
      }
      if (key === ".") {
        if (raw.includes(".")) {
          return;
        }
        this.amountInput = raw ? `${raw}.` : "0.";
        return;
      }
      if (raw.includes(".")) {
        const decimals = raw.split(".")[1] || "";
        if (decimals.length >= 2) {
          return;
        }
      }
      raw = `${raw}${key}`;
      this.amountInput = raw;
    },
    deleteAmount() {
      let raw = String(this.amountInput || "");
      if (!raw || raw === "0.00") {
        this.amountInput = "0.00";
        return;
      }
      raw = raw.slice(0, -1);
      if (!raw || raw === "0" || raw === "0.") {
        this.amountInput = "0.00";
        return;
      }
      this.amountInput = raw;
    },
    clearAll() {
      this.selectedGiftKeys = [];
      this.amountInput = "0.00";
      this.numPadVisible = false;
    },
    async submitOrder() {
      if (this.submitDisabled) {
        this.$toast(this.copy.selectGiftError);
        return;
      }
      if (Number(this.balanceText) < this.totalAmount) {
        this.$toast(this.copy.insufficientBalance);
        return;
      }
      this.submitting = true;
      const itemNames = this.selectedGifts.map((item) => item.type).join(",");
      const res = await this.$http({
        method: "post",
        data: {
          item: itemNames,
          money: this.formatMoney(this.amountValue),
          lid: this.$route.query.id || this.lottery.id,
          mid: this.userInfo.id,
          expect: this.lottery.now_expect || this.lottery.next_expect || "",
        },
        url: "game_place_order",
      });
      this.submitting = false;
      if (res && res.code === 200) {
        this.$toast(res.msg || this.copy.submitSuccess);
        this.clearAll();
        this.fetchPageData();
        return;
      }
      this.$toast((res && res.msg) || this.copy.submitFailed);
    },
  },
};
</script>

<style lang="less" scoped>
.boost-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 50% 0, rgba(165, 92, 255, 0.14), transparent 26%),
    linear-gradient(180deg, #0a0710 0%, #05030b 100%);
  color: #fff;
}

.boost-shell {
  width: 100%;
  max-width: 23.4375rem;
  margin: 0 auto;
  padding: 0 0.75rem 7.1rem;
}

.topbar {
  display: grid;
  grid-template-columns: 2.5rem 1fr 2.5rem;
  align-items: center;
  gap: 0.75rem;
  padding-top: max(0.875rem, env(safe-area-inset-top));
}

.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.9rem;
  background: rgba(19, 13, 27, 0.94);
  color: #fff;
}

.back-btn svg {
  width: 1.05rem;
  height: 1.05rem;
}

.topbar h1 {
  margin: 0;
  text-align: center;
  font-size: 1.18rem;
  font-weight: 800;
}

.head-spacer {
  display: block;
  width: 2.5rem;
}

.boost-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
  margin-top: 0.85rem;
  padding: 0.7rem;
  border: 1px solid rgba(255, 93, 149, 0.18);
  border-radius: 1.4rem;
  background: linear-gradient(180deg, rgba(60, 21, 71, 0.95), rgba(36, 15, 44, 0.92));
}

.round-toggle,
.selected-box {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.8rem 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1.1rem;
  background: rgba(43, 24, 52, 0.6);
}

.round-toggle {
  position: relative;
  text-align: left;
  color: #fff;
}

.round-copy {
  display: flex;
  flex-direction: column;
  gap: 0.42rem;
}

.round-copy span,
.selected-box span {
  color: rgba(255, 215, 236, 0.82);
  font-size: 0.72rem;
  font-weight: 700;
}

.countdown {
  color: #ff7aaa;
  font-size: 1.05rem;
  line-height: 1;
}

.round-arrow {
  position: absolute;
  top: 50%;
  right: 0.9rem;
  width: 1.5rem;
  height: 1.5rem;
  margin-top: -0.75rem;
  border-radius: 50%;
  border: 1px solid rgba(255, 120, 170, 0.4);
}

.round-arrow::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0.42rem;
  height: 0.42rem;
  border-right: 0.125rem solid #ff9ec2;
  border-bottom: 0.125rem solid #ff9ec2;
  transform: translate(-50%, -65%) rotate(45deg);
  transition: transform 0.25s ease;
}

.round-arrow.open::before {
  transform: translate(-50%, -30%) rotate(-135deg);
}

.selected-box {
  align-items: stretch;
  gap: 0.55rem;
}

.selected-box b {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.05rem;
  padding: 0.3rem 0.5rem;
  border-radius: 0.85rem;
  background: rgba(31, 18, 38, 0.9);
  color: #fff;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.35;
  text-align: center;
}

.history-panel {
  margin-top: 0.75rem;
  border: 1px solid rgba(255, 93, 149, 0.18);
  border-radius: 1.25rem;
  background: rgba(24, 14, 31, 0.92);
  overflow: hidden;
}

.history-head,
.history-row {
  display: grid;
  grid-template-columns: 6.35rem minmax(0, 1fr) 4.35rem;
  gap: 0.6rem;
  align-items: center;
  padding: 0.72rem 1rem;
}

.history-head {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.72rem;
  font-weight: 700;
}

.history-list {
  max-height: 16rem;
  overflow-y: auto;
}

.history-row {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 0.76rem;
}

.history-row b {
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-row span {
  color: #ff93bf;
  font-weight: 700;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-row time {
  color: rgba(255, 255, 255, 0.64);
  text-align: right;
  white-space: nowrap;
}

.history-head span:nth-child(2) {
  text-align: center;
}

.gift-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 0.75rem;
}

.gift-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 0.9rem 0.35rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(20, 13, 28, 0.96);
  color: #fff;
}

.gift-btn.on {
  border-color: rgba(255, 93, 149, 0.5);
  box-shadow: inset 0 0 0 1px rgba(255, 93, 149, 0.18);
}

.gift-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 0.8rem;
  background: rgba(255, 255, 255, 0.07);
  font-size: 1.3rem;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif !important;
  font-weight: 400 !important;
  line-height: 1;
}

.gift-name {
  font-size: 0.76rem;
  font-weight: 700;
}

.boost-banner {
  margin-top: 0.8rem;
  border-radius: 1.35rem;
  overflow: hidden;
}

.boost-banner img {
  width: 100%;
  display: block;
  aspect-ratio: 1.54 / 1;
  object-fit: cover;
}

.amount-wrap {
  margin-top: 1rem;
  padding: 0.8rem;
  border: 1px solid rgba(255, 93, 149, 0.18);
  border-radius: 1.45rem;
  background: linear-gradient(180deg, rgba(32, 16, 42, 0.95), rgba(18, 11, 27, 0.96));
}

.balance-head {
  display: flex;
}

.balance-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 2.15rem;
  padding: 0 0.8rem;
  border: 1px solid rgba(255, 93, 149, 0.26);
  border-radius: 0.95rem;
  background: rgba(39, 22, 48, 0.76);
}

.balance-pill span {
  color: #ffa8cb;
  font-size: 0.7rem;
  font-weight: 700;
}

.balance-pill b {
  font-size: 0.82rem;
}

.amount-row {
  display: grid;
  grid-template-columns: 1fr 4.75rem;
  gap: 0.55rem;
  margin-top: 0.75rem;
}

.amount-input,
.currency {
  display: flex;
  align-items: center;
  min-height: 3.15rem;
  border: 1px solid rgba(255, 93, 149, 0.36);
  border-radius: 1rem;
  background: rgba(29, 18, 37, 0.9);
}

.amount-input {
  padding: 0 1rem;
  justify-content: flex-start;
  color: #fff;
  font-size: 1.02rem;
  font-weight: 800;
}

.currency {
  justify-content: center;
  color: #ff85b2;
  font-size: 0.92rem;
  font-weight: 800;
}

.amount-summary {
  display: grid;
  grid-template-columns: 4.5rem 1fr 7rem;
  gap: 0.65rem;
  align-items: center;
  margin-top: 0.85rem;
}

.clear-btn,
.submit-btn {
  min-height: 2.9rem;
  border-radius: 1rem;
  font-size: 0.94rem;
  font-weight: 800;
}

.clear-btn {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.88);
}

.total-desc {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
}

.total-desc em {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.72rem;
  font-style: normal;
}

.total-desc b {
  color: #ff96c0;
  font-size: 0.86rem;
}

.submit-btn {
  border: 0;
  background: linear-gradient(135deg, #ff6da3, #ff417f);
  box-shadow: 0 0.9rem 1.8rem rgba(255, 71, 128, 0.25);
  color: #fff;
}

.submit-btn.disabled {
  opacity: 0.45;
  box-shadow: none;
}

.num-pad-mask {
  background: rgba(8, 4, 12, 0.58);
}

.num-pad {
  width: min(100%, 25rem);
  margin: 0 auto;
  padding: 0.9rem 0.85rem calc(1rem + env(safe-area-inset-bottom));
  border-radius: 1.6rem 1.6rem 0 0;
  background: linear-gradient(180deg, rgba(33, 17, 44, 0.98), rgba(14, 9, 23, 0.98));
}

.num-pad-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.num-pad-head span {
  font-size: 1rem;
  font-weight: 700;
}

.num-pad-close {
  min-width: 3.1rem;
  height: 2rem;
  border: 1px solid rgba(255, 93, 149, 0.5);
  border-radius: 999px;
  background: transparent;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
}

.num-pad-display {
  display: flex;
  align-items: center;
  min-height: 3rem;
  margin-top: 0.85rem;
  padding: 0 0.95rem;
  border: 1px solid rgba(255, 93, 149, 0.36);
  border-radius: 1rem;
  background: rgba(31, 19, 40, 0.88);
  color: #fff;
  font-size: 1.1rem;
  font-weight: 800;
}

.num-pad-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.55rem;
  margin-top: 0.75rem;
}

.num-key {
  min-height: 3rem;
  border: 0;
  border-radius: 0.95rem;
  background: rgba(44, 30, 53, 0.86);
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
}

.num-key svg {
  width: 1.35rem;
  height: 1.35rem;
}

.num-key.clear {
  background: rgba(61, 45, 70, 0.86);
}

.num-key.ok {
  grid-column: span 2;
  background: linear-gradient(135deg, #ff6da3, #ff417f);
}

button {
  cursor: pointer;
}

@media (max-width: 47.9375rem) {
  .boost-shell {
    max-width: 24.25rem;
    padding-left: 0.625rem;
    padding-right: 0.625rem;
  }
}

@media (max-width: 22.5rem) {
  .boost-shell {
    max-width: 23.5rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .boost-panel {
    gap: 0.5rem;
    padding: 0.55rem;
  }

  .gift-grid {
    gap: 0.5rem;
  }

  .amount-summary {
    grid-template-columns: 4.1rem 1fr 6.25rem;
  }
}

@media (min-width: 48rem) {
  .boost-shell {
    max-width: 25rem;
  }
}
</style>
